import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Download, Camera } from 'lucide-react';

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'projects', label: 'Projects' },
    { id: 'skills', label: 'Skills' },
    { id: 'certifications', label: 'Certifications' },
    { id: 'contact', label: 'Contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    const handleSectionChange = () => {
      const sections = navItems.map(item => document.getElementById(item.id));
      const scrollPos = window.scrollY + 120;
      sections.forEach((section, index) => {
        if (section) {
          const top = section.offsetTop;
          const bottom = top + section.offsetHeight;
          if (scrollPos >= top && scrollPos <= bottom) {
            setActiveSection(navItems[index].id);
          }
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleSectionChange);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleSectionChange);
    };
  }, [navItems]);

  const scrollToSection = (sectionId: string) => {
    setIsMobileMenuOpen(false);
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' });
    }, 100);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-4 px-4 sm:px-6"
      >
        <motion.div
          animate={{
            backgroundColor: isScrolled ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.4)',
            backdropFilter: 'blur(24px)',
            boxShadow: isScrolled ? '0 10px 40px rgba(0,0,0,0.05)' : '0 4px 20px rgba(0,0,0,0.02)',
          }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className="w-full max-w-5xl rounded-full px-4 sm:px-6 border border-white/90"
        >
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <motion.button
              onClick={() => scrollToSection('home')}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center gap-3 group"
            >
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-slate-700 font-bold text-lg"
                style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.05)' }}
              >
                A
              </div>
              <div className="hidden sm:flex items-center gap-1.5">
                <span
                  className="text-sm font-semibold tracking-tight text-slate-800"
                  style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
                >
                  AMIT
                </span>
                <span
                  className="text-sm font-medium tracking-tight text-slate-400"
                  style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
                >
                  ADHIKARY
                </span>
              </div>
            </motion.button>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <motion.button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-full"
                  style={{
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    color: activeSection === item.id ? '#0ea5e9' : '#475569',
                  }}
                >
                  {activeSection === item.id && (
                    <motion.div
                      layoutId="nav-active-indicator"
                      className="absolute inset-0 rounded-full shadow-sm"
                      style={{ backgroundColor: 'rgba(14, 165, 233, 0.1)', border: '1px solid rgba(14, 165, 233, 0.2)' }}
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10">{item.label}</span>
                </motion.button>
              ))}
            </div>

            {/* Action buttons */}
            <div className="hidden md:flex items-center gap-3">
              <motion.a
                href="https://photos.amitadhikary.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.04, backgroundColor: 'rgba(71,85,105,0.05)' }}
                whileTap={{ scale: 0.96 }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 text-[#475569] hover:text-[#0ea5e9]"
                style={{
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  border: '1px solid rgba(0,0,0,0.05)',
                  backgroundColor: 'rgba(0,0,0,0.02)',
                }}
              >
                <Camera size={14} />
                <span>Clicks</span>
              </motion.a>

              <motion.a
                href="/assets/images/Amit_Adhikary_resume.pdf"
                download
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.04, backgroundColor: 'rgba(14,165,233,0.15)' }}
                whileTap={{ scale: 0.96 }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full text-[#0ea5e9] transition-all duration-200 shadow-sm"
                style={{
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  background: 'rgba(14,165,233,0.1)',
                  border: '1px solid rgba(14,165,233,0.2)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Download size={14} />
                <span>Resume</span>
              </motion.a>
            </div>

            {/* Mobile toggle */}
            <div className="md:hidden flex items-center">
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 text-slate-700"
                style={{
                  background: isMobileMenuOpen ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.02)',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}
                aria-label="Toggle menu"
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={isMobileMenuOpen ? 'close' : 'open'}
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {isMobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
                  </motion.div>
                </AnimatePresence>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="fixed top-24 left-4 right-4 z-40 rounded-[28px] p-4 shadow-2xl overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(24px) saturate(180%)',
              border: '1px solid rgba(0,0,0,0.05)',
              boxShadow: '0 24px 64px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,1)',
            }}
          >
            <div className="flex flex-col gap-1.5">
              {navItems.map((item, i) => (
                <motion.button
                  key={item.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => scrollToSection(item.id)}
                  className="relative w-full text-left px-5 py-3.5 rounded-2xl text-[15px] font-medium transition-all duration-200"
                  style={{
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    color: activeSection === item.id ? '#0ea5e9' : '#475569',
                    backgroundColor: activeSection === item.id ? 'rgba(14, 165, 233, 0.08)' : 'transparent',
                  }}
                >
                  <span className="flex items-center gap-3">
                    {activeSection === item.id && (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#0ea5e9] shadow-[0_0_8px_rgba(14,165,233,0.4)]" />
                    )}
                    {item.label}
                  </span>
                </motion.button>
              ))}

              <div className="border-t my-2 border-black/5 mx-2" />

              <motion.a
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: navItems.length * 0.04 }}
                href="https://photos.amitadhikary.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-5 py-3.5 rounded-2xl text-[15px] font-medium transition-all duration-200 text-[#475569] hover:bg-slate-50"
                style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
              >
                <Camera size={18} />
                <span>Clicks</span>
              </motion.a>

              <motion.a
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: (navItems.length + 1) * 0.04 }}
                href="/assets/images/Amit_Adhikary_resume.pdf"
                download
                target="_blank"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-5 py-3.5 rounded-2xl text-[15px] font-medium transition-all duration-200 text-[#0ea5e9] bg-[rgba(14,165,233,0.1)] border border-[rgba(14,165,233,0.2)] shadow-sm"
                style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
              >
                <Download size={18} />
                <span>Download Resume</span>
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;